import React, { useState, useEffect, useCallback, useRef } from 'react';
import { ReadingWisdom, ShareBox } from '../ReadingFooter/ReadingFooter';
import { readingCopy } from '../../data/readingCopy';
import './CardResults.css';

const CardResults = ({ cards, verdict, interpretation, isStreaming, compact, isStreamMode = false, hideShare = false, lang }) => {
  const copy = readingCopy(lang);
  const [flipped, setFlipped] = useState([false, false, false, false]);
  const loadingRef = useRef(null);
  const interpretationBoxRef = useRef(null);

  const flipSequentially = useCallback(() => {
    [0, 1, 2, 3].forEach((i) => {
      setTimeout(() => {
        setFlipped((prev) => {
          const next = [...prev];
          next[i] = true;
          return next;
        });
      }, i * 350);
    });
  }, []);

  useEffect(() => {
    if (cards && cards.length === 4) {
      setFlipped([false, false, false, false]);
      const timer = setTimeout(flipSequentially, 100);
      return () => clearTimeout(timer);
    }
  }, [cards, flipSequentially]);

  useEffect(() => {
    if (interpretationBoxRef.current && interpretation) {
      interpretationBoxRef.current.scrollTop = interpretationBoxRef.current.scrollHeight;
    }
  }, [interpretation]);


  const polarityClass = (polarity) => {
    if (!polarity) return 'cr-polarity-neutral';
    if (polarity.includes('Positive')) return 'cr-polarity-positive';
    if (polarity.includes('Negative')) return 'cr-polarity-negative';
    return 'cr-polarity-neutral';
  };

  if (!cards || cards.length === 0) return null;

  return (
    <div className={`card-results ${compact ? 'card-results-compact' : ''} ${isStreamMode ? 'card-results-stream' : ''}`}>
      <div className="cr-cards-row">
        {cards.map((card, i) => (
          <div key={`${card.imageCode}-${i}`} className="cr-card-wrapper">
            <div className={`cr-card ${flipped[i] ? 'flipped' : ''}`}>
              <div className="cr-card-inner">
                <div className="cr-card-back">
                  <div className="cr-card-back-design">
                    <span className="cr-card-back-symbol">✦</span>
                  </div>
                </div>
                <div
                  className={`cr-card-front ${card.orientation === 'Reversed' ? 'reversed' : ''}`}
                  style={{
                    backgroundImage: `url(/tarot-reader/images/small/${card.imageCode}.png)`,
                  }}
                />
              </div>
            </div>
            <div className={`cr-card-info ${flipped[i] ? 'visible' : ''}`}>
              <span className="cr-card-name">{card.name}</span>
              <span className={`cr-card-orientation ${card.orientation === 'Reversed' ? 'reversed-label' : ''}`}>
                {card.orientation}
              </span>
              <span className={`cr-card-polarity ${polarityClass(card.polarity)}`}>
                {card.polarity}
              </span>
            </div>
          </div>
        ))}
      </div>

      {verdict && !(isStreamMode && interpretation) && (
        <div className="cr-verdict">
          {copy.outlook} <strong>{verdict}</strong>
        </div>
      )}

      {isStreaming && !interpretation && (
        <div className="cr-loading-bar" ref={loadingRef}>
          {isStreamMode && <span className="cr-live-badge">{copy.live}</span>}
          <div className="cr-loading-track">
            <div className="cr-loading-shimmer" />
          </div>
          <span className="cr-loading-text">{copy.analyzing}</span>
        </div>
      )}

      {interpretation && (
        <div
          className={`cr-interpretation ${isStreaming ? 'streaming' : ''} ${isStreamMode ? 'stream-mode' : ''}`}
          ref={interpretationBoxRef}
        >
          {isStreamMode && (
            <div className="cr-interpretation-header">
              <span className="cr-live-badge">{isStreaming ? copy.live : copy.complete}</span>
              {verdict && (
                <span className="cr-verdict-inline">
                  {copy.outlook} <strong>{verdict}</strong>
                </span>
              )}
            </div>
          )}
          <p>
            {interpretation}
            {isStreaming && <span className="cr-cursor">|</span>}
          </p>
        </div>
      )}

      {interpretation && !isStreaming && (
        <>
          <ReadingWisdom lang={lang} />
          {!compact && !hideShare && <ShareBox lang={lang} />}
        </>
      )}

      {cards.some((c) => c.description) && !interpretation && !isStreaming && (
        <div className="cr-meanings">
          {cards.map((card, i) => (
            <div key={i} className="cr-meaning-card">
              <div className="cr-meaning-header">
                <img
                  src={`/tarot-reader/images/small/${card.imageCode}.png`}
                  alt={card.name}
                  className="cr-meaning-img"
                />
                <div>
                  <h4 className="cr-meaning-name">{card.name}</h4>
                  <span className="cr-meaning-keywords">
                    {card.keywords ? card.keywords.join(' · ') : ''}
                  </span>
                </div>
              </div>
              <p className="cr-meaning-text">{card.description}</p>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

export default CardResults;
